import { useCallback, useLayoutEffect, useRef, useState } from 'react';

// Where each list was scrolled to, for this visit to the app.
const positions = new Map<string, number>();

/**
 * A list comes back where it was left, as in WhatsApp (Gary, 16 Oct). On a phone a list is
 * taken off the screen while a chat is open, so without this it went back to the top.
 * Put the returned ref on the list's scrolling element.
 */
export function useKeptScroll<T extends HTMLElement>(key: string) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const saved = positions.get(key);
    if (saved) el.scrollTop = saved;
    const save = () => positions.set(key, el.scrollTop);
    el.addEventListener('scroll', save, { passive: true });
    return () => el.removeEventListener('scroll', save);
  }, [key]);
  return ref;
}

// A screen's own choices, for this visit to the app.
const values = new Map<string, unknown>();

/**
 * State that comes back with the screen, as its scroll does (mobile review, 19 Oct: going back from a search
 * result opened the list on Reply with the search gone). Used like useState with a plain value.
 */
export function useKept<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => (values.has(key) ? (values.get(key) as T) : initial));
  const set = useCallback((next: T) => { values.set(key, next); setValue(next); }, [key]);
  return [value, set];
}
